// src/pages/team/CoinDemos.jsx
// Coin « Démos » — lanceur des parcours de démonstration à présenter aux prospects.
// Accès : route staff + coin 'demos' (l'admin a tous les coins).
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { hasCoin, COIN_BY_KEY } from "../../config/teamCoins";

const COIN = COIN_BY_KEY.demos;

// Parcours de démonstration disponibles dans l'app (routes existantes).
const DEMOS = [
  { title: "Plateforme ARTKIV", desc: "Tour produit complet — idéal pour un premier rendez-vous.", route: "/explore", accent: "violet" },
  { title: "Art Advisor", desc: "Espace multi-clients pour conseiller en art (cible campagne septembre).", route: "/plans/art-advisor/demo", accent: "amber" },
  { title: "Family Office", desc: "Gestion patrimoniale et transmission pour family offices.", route: "/plans/family-office/demo", accent: "blue" },
  { title: "Institution / Fondation", desc: "Parcours institutionnel et dossiers de collection.", route: "/demo/institution", accent: "slate" },
  { title: "Assurance — AXA", desc: "Parcours courtier assurance (dossier de valeur).", route: "/insurance-demo/axa", accent: "green" },
  { title: "Transport — Préforum", desc: "Qualification logistique et transport d'œuvres.", route: "/transport-demo/preforum", accent: "rose" },
];

const DOT = {
  violet: "bg-violet-500", amber: "bg-amber-500", blue: "bg-blue-500",
  slate: "bg-gray-400", green: "bg-emerald-500", rose: "bg-rose-500",
};

export default function CoinDemos() {
  const { profile, profileLoading } = useAuth();
  const allowed = hasCoin(profile, "demos");

  if (profileLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f6f6f4]"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-gray-900" /></div>;
  }
  if (!allowed) {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <div className="text-4xl" aria-hidden>🔒</div>
        <h1 className="mt-4 font-serif text-2xl font-semibold text-gray-900">Accès non accordé</h1>
        <p className="mt-2 text-sm text-gray-600">Le coin « Démos » ne vous a pas été attribué. Demandez-le à un administrateur.</p>
        <Link to="/team/dashboard" className="mt-6 inline-block rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800">Retour à mon espace</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f4]">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="text-3xl" aria-hidden>{COIN.emoji}</span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Coin Démos</p>
              <h1 className="font-serif text-2xl font-semibold text-gray-950">{COIN.title}</h1>
            </div>
          </div>
          <Link to="/team/dashboard" className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">← Mon espace</Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-7">
        <p className="mb-5 max-w-2xl text-sm text-gray-600">
          Lancez le parcours de démonstration adapté à votre interlocuteur. Chaque démo s'ouvre dans un
          environnement de présentation — sans impacter les données réelles des clients.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {DEMOS.map((d) => (
            <Link key={d.route} to={d.route}
              className="group rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${DOT[d.accent]}`} />
                <h3 className="font-semibold text-gray-950">{d.title}</h3>
              </div>
              <p className="mt-2 text-sm text-gray-500">{d.desc}</p>
              <span className="mt-4 inline-block text-sm font-medium text-gray-900 group-hover:underline">Ouvrir la démo →</span>
            </Link>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-blue-100 bg-blue-50 p-5 text-sm text-blue-900">
          <strong>Conseil.</strong> Préparez la démo à l'avance : ouvrez le bon parcours, vérifiez que les
          exemples s'affichent, et gardez le CRM sous la main pour créer le lead et planifier la relance juste après.
        </div>
      </main>
    </div>
  );
}
